import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getStoreListApi } from '~/features/store/api/get-store-list.api';
import { getStoreApi } from '~/features/store/api/get-store.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type { Store, StoreListParams } from '~/features/store/types/store.type';

export interface StoreState {
  isLoading: boolean;
  list: Store[];
  meta: PaginationMeta | null;
  params: StoreListParams | null;
  selectedStore: Store | null;
}

const initialState: StoreState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedStore: null,
};

/**
 * 매장 목록 조회
 */
export const fetchStoreList = createAsyncThunk<
  PaginatedApiResponse<Store>,
  StoreListParams | undefined
>('store/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getStoreListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * 매장 상세 조회
 */
export const fetchStoreDetail = createAsyncThunk<Store, number>(
  'store/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getStoreApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const StoreSlice = createSlice({
  initialState,
  name: 'store',
  reducers: {
    setSelectedStore: (state, action) => {
      state.selectedStore = action.payload;
    },
    resetSelectedStore: state => {
      state.selectedStore = null;
    },
    resetStore: state => {
      state.selectedStore = null;
      state.list = [];
      state.meta = null;
      state.params = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // fetchStoreList
      .addCase(fetchStoreList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchStoreList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchStoreList.rejected, state => {
        state.isLoading = false;
      })
      // fetchStoreDetail
      .addCase(fetchStoreDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchStoreDetail.fulfilled, (state, action) => {
        state.selectedStore = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchStoreDetail.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const { setSelectedStore, resetSelectedStore, resetStore } = StoreSlice.actions;
export default StoreSlice;
